import React from 'react'
import {
  getWeekNumberAndYear,
  getDateOfISOWeek,
  formatToLocalDate,
} from '../../../../../shared/libraries/utils/date'
import startOfWeek from 'date-fns/start_of_week'
import { useFormChildLogic } from '../../../index'
import InlineSelect from '../InlineSelect/InlineSelect'

/**
 * Wrapper around <SelectDashboard /> that contains logic for generating week select options and their formatting on save
 * Operates all dates as local dates (from formatToLocalDate function)
 * @param {function} props.onChange Will be called when user chooses option item
 * @returns {JSX.Element}
 * @example <WeekSelect onChange={() => {}}/>
 */
const WeekSelect = ({
  onChange,
  value = new Date(),
  name,
  disabled,
  label,
  initValue,
}) => {
  const options = getWeekSelectOptions(value)
  const { updateForm } = useFormChildLogic({
    name: name,
    initValue: initValue,
  })
  const handleSetWeek = (event, index, date) => {
    onChange && onChange(date)
    updateForm(date)
  }
  const formattedValue = formatToLocalDate(
    startOfWeek(new Date(value), { weekStartsOn: 1 }),
  )

  return (
    <InlineSelect
      label={label ? label : 'Week'}
      disabled={disabled}
      value={formattedValue}
      name={'weekDate'}
      options={options}
      onChange={handleSetWeek}
    />
  )
}

/**
 * Return object that can be consumed by SelectDashboard as option
 * @param {number} week Week number (1-53)
 * @param {number} year Year
 *
 * @returns {object} { label: '15 Week 2020 Year', value: <DateISO> }
 *
 * @example generateSelectItem(15, 2020)
 */
const generateSelectItem = (week, year) => ({
  label: `W${week}, ${year}`,
  value: formatToLocalDate(getDateOfISOWeek(week, year)),
})

/**
 * Generates list of weeks started fromm current week and WEEKS_IN_FUTURE to the future
 * Example: If now is 15th week then this function will return weeks from 15 to 25 (current week + 10)
 * If future weeks are overflowing to the next year it will handle it and start counting overflowing weeks for the next year
 * Example: Current week is 50 and year is 2020 then it will return 50-53 weeks for 2020 and 1-7 weeks for 2021
 *
 * Returns array of options that can be consumed by <SelectDashboard />
 *
 * @returns {array} [
 *  { label: '53 Week 2020 Year', value: <DateISO> },
 *  { label: '1 Week 2021 Year', value: <DateISO> }
 * ]
 */
const getWeekSelectOptions = value => {
  const WEEKS_IN_FUTURE = 10

  const { weekNumber, year } = getWeekNumberAndYear(new Date())

  let weekSelectOptions = []

  for (let week = weekNumber; week < weekNumber + WEEKS_IN_FUTURE; week++) {
    if (week > 53) {
      weekSelectOptions.push(generateSelectItem(week - 53, year + 1))
    } else {
      weekSelectOptions.push(generateSelectItem(week, year))
    }
  }

  const {
    weekNumber: currentValueWeek,
    year: currentValueYear,
  } = getWeekNumberAndYear(new Date(value))

  const currentValueItem = generateSelectItem(
    currentValueWeek,
    currentValueYear,
  )

  if (!weekSelectOptions.find(item => item.value === currentValueItem.value)) {
    weekSelectOptions.unshift(currentValueItem)
  }

  return weekSelectOptions
}

export default WeekSelect
